import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../services/api";

// Import logo - place your logo files in src/assets/
import uptechLogo from "../assets/uptech-logo.png";

export default function Register() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setError("");
    
    // Calculate password strength
    if (name === "password") {
      let strength = 0;
      if (value.length >= 8) strength++;
      if (/[a-z]/.test(value) && /[A-Z]/.test(value)) strength++;
      if (/\d/.test(value)) strength++;
      if (/[^a-zA-Z0-9]/.test(value)) strength++;
      setPasswordStrength(strength);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.fullName || !form.email || !form.password || !form.confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (form.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      const response = await api.post("/Auth/register", {
        fullName: form.fullName,
        email: form.email,
        password: form.password
      });

      setSuccess(true);

      setTimeout(() => {
        navigate("/login", {
          state: {
            message: "Registration successful! Please login with your credentials.",
            email: form.email
          }
        });
      }, 2000);

    } catch (err) {
      console.error("Registration error:", err);
      const message = err.response?.data?.message
        || err.response?.data?.errors?.join(", ")
        || err.response?.data
        || "Registration failed. Please try again.";
      setError(typeof message === 'string' ? message : JSON.stringify(message));
    } finally {
      setLoading(false);
    }
  };

  const getStrengthColor = () => {
    if (passwordStrength <= 1) return "#ef4444";
    if (passwordStrength <= 2) return "#F5A623";
    return "#10b981";
  };

  const getStrengthText = () => {
    if (!form.password) return "";
    if (passwordStrength <= 1) return "Weak";
    if (passwordStrength <= 2) return "Medium";
    return "Strong";
  };

  const styles = {
    container: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#011745",
      padding: "20px",
      position: "relative",
      overflow: "hidden",
      fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    },
    backgroundGlow1: {
      position: "absolute",
      top: "5%",
      right: "-25%",
      width: "70%",
      height: "70%",
      background: "radial-gradient(ellipse at center, rgba(88, 214, 190, 0.12) 0%, transparent 65%)",
      pointerEvents: "none"
    },
    backgroundGlow2: {
      position: "absolute",
      bottom: "-15%",
      left: "-15%",
      width: "55%",
      height: "55%",
      background: "radial-gradient(ellipse at center, rgba(17, 131, 251, 0.1) 0%, transparent 55%)",
      pointerEvents: "none"
    },
    card: {
      background: "white",
      borderRadius: "20px",
      padding: "44px 40px",
      width: "100%",
      maxWidth: "460px",
      boxShadow: "0 25px 50px rgba(0,0,0,0.3)",
      position: "relative",
      zIndex: 1
    },
    logo: {
      textAlign: "center",
      marginBottom: "28px"
    },
    logoImage: {
      height: "50px",
      width: "auto"
    },
    title: {
      fontSize: "28px",
      fontWeight: "800",
      color: "#011745",
      marginBottom: "8px",
      textAlign: "center"
    },
    subtitle: {
      color: "#64748b",
      textAlign: "center",
      marginBottom: "28px",
      fontSize: "15px"
    },
    progressSteps: {
      display: "flex",
      gap: "8px",
      marginBottom: "28px"
    },
    step: {
      flex: 1,
      height: "4px",
      background: "#e8edf5",
      borderRadius: "2px"
    },
    stepActive: {
      background: "linear-gradient(90deg, #1183FB, #58D6BE)"
    },
    formGroup: {
      marginBottom: "20px"
    },
    label: {
      display: "block",
      fontWeight: "600",
      color: "#011745",
      marginBottom: "8px",
      fontSize: "14px"
    },
    required: {
      color: "#ef4444"
    },
    inputWrapper: {
      position: "relative"
    },
    input: {
      width: "100%",
      padding: "14px 18px",
      paddingLeft: "46px",
      border: "2px solid #e8edf5",
      borderRadius: "12px",
      fontSize: "15px",
      transition: "all 0.3s ease",
      boxSizing: "border-box",
      background: "#f7f9fc",
      color: "#011745",
      fontFamily: "inherit"
    },
    inputIcon: {
      position: "absolute",
      left: "14px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#94a3b8",
      width: "20px",
      height: "20px"
    },
    passwordToggle: {
      position: "absolute",
      right: "14px",
      top: "50%",
      transform: "translateY(-50%)",
      background: "none",
      border: "none",
      color: "#94a3b8",
      cursor: "pointer",
      padding: "4px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    passwordStrength: {
      display: "flex",
      gap: "6px",
      marginTop: "10px"
    },
    strengthBar: {
      flex: 1,
      height: "4px",
      background: "#e8edf5",
      borderRadius: "2px",
      transition: "all 0.3s ease"
    },
    strengthText: {
      fontSize: "12px",
      marginTop: "6px",
      fontWeight: "500"
    },
    button: {
      width: "100%",
      padding: "16px",
      background: "linear-gradient(135deg, #1183FB 0%, #0d6edb 100%)",
      color: "white",
      border: "none",
      borderRadius: "12px",
      fontSize: "16px",
      fontWeight: "700",
      cursor: "pointer",
      transition: "all 0.3s ease",
      marginTop: "8px",
      fontFamily: "inherit"
    },
    buttonDisabled: {
      opacity: 0.7,
      cursor: "not-allowed"
    },
    error: {
      background: "#fef2f2",
      border: "1px solid #fecaca",
      color: "#dc2626",
      padding: "14px 16px",
      borderRadius: "12px",
      marginBottom: "20px",
      fontSize: "14px",
      display: "flex",
      alignItems: "center",
      gap: "10px"
    },
    success: {
      background: "#f0fdf4",
      border: "1px solid #bbf7d0",
      color: "#16a34a",
      padding: "24px",
      borderRadius: "12px",
      marginBottom: "20px",
      textAlign: "center"
    },
    successIcon: {
      fontSize: "48px",
      marginBottom: "12px"
    },
    successTitle: {
      fontSize: "20px",
      fontWeight: "bold",
      marginBottom: "8px",
      color: "#16a34a"
    },
    successText: {
      fontSize: "14px",
      color: "#15803d"
    },
    link: {
      color: "#1183FB",
      textDecoration: "none",
      fontWeight: "600"
    },
    footer: {
      textAlign: "center",
      marginTop: "24px",
      color: "#64748b",
      fontSize: "14px"
    },
    spinner: {
      display: "inline-block",
      width: "20px",
      height: "20px",
      border: "2px solid rgba(255,255,255,0.3)",
      borderRadius: "50%",
      borderTopColor: "white",
      animation: "spin 0.8s linear infinite",
      marginRight: "10px",
      verticalAlign: "middle"
    }
  };

  // Success State
  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.backgroundGlow1}></div>
        <div style={styles.backgroundGlow2}></div>
        <div style={styles.card}>
          <div style={styles.logo}>
            <img src={uptechLogo} alt="Uptech Innovations Ltd" style={styles.logoImage} />
          </div>
          <div style={styles.success}>
            <div style={styles.successIcon}>✓</div>
            <div style={styles.successTitle}>Registration Successful!</div>
            <div style={styles.successText}>
              Your account has been created successfully.<br />
              Redirecting to login page...
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <Link to="/login" style={styles.link}>Click here if not redirected</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          input:focus {
            border-color: #1183FB !important;
            box-shadow: 0 0 0 4px rgba(17, 131, 251, 0.1) !important;
            outline: none !important;
            background: white !important;
          }
          button:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 8px 24px rgba(17, 131, 251, 0.35);
          }
        `}
      </style>

      <div style={styles.backgroundGlow1}></div>
      <div style={styles.backgroundGlow2}></div>

      <div style={styles.card}>
        <div style={styles.logo}>
          <img src={uptechLogo} alt="Uptech Innovations Ltd" style={styles.logoImage} />
        </div>
        <h1 style={styles.title}>Create Account</h1>
        <p style={styles.subtitle}>Join Uptech and start banking smarter</p>

        <div style={styles.progressSteps}>
          <div style={{...styles.step, ...styles.stepActive}}></div>
          <div style={styles.step}></div>
          <div style={styles.step}></div>
        </div>

        {error && (
          <div style={styles.error}>
            <span>!</span>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Full Name <span style={styles.required}>*</span>
            </label>
            <div style={styles.inputWrapper}>
              <input
                type="text"
                name="fullName"
                style={styles.input}
                placeholder="Enter your full name"
                value={form.fullName}
                onChange={handleChange}
                disabled={loading}
                required
              />
              <svg style={styles.inputIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Email <span style={styles.required}>*</span>
            </label>
            <div style={styles.inputWrapper}>
              <input
                type="email"
                name="email"
                style={styles.input}
                placeholder="Enter your email"
                value={form.email}
                onChange={handleChange}
                disabled={loading}
                required
              />
              <svg style={styles.inputIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Password <span style={styles.required}>*</span>
            </label>
            <div style={styles.inputWrapper}>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                style={styles.input}
                placeholder="Create a strong password"
                value={form.password}
                onChange={handleChange}
                disabled={loading}
                required
                minLength={6}
              />
              <svg style={styles.inputIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              <button 
                type="button" 
                style={styles.passwordToggle}
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            {form.password && (
              <>
                <div style={styles.passwordStrength}>
                  {[1, 2, 3, 4].map((i) => (
                    <div 
                      key={i} 
                      style={{
                        ...styles.strengthBar,
                        background: i <= passwordStrength ? getStrengthColor() : "#e8edf5"
                      }}
                    />
                  ))}
                </div>
                <div style={{...styles.strengthText, color: getStrengthColor()}}>
                  {getStrengthText()} password
                </div>
              </>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Confirm Password <span style={styles.required}>*</span>
            </label>
            <div style={styles.inputWrapper}>
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                style={styles.input}
                placeholder="Confirm your password"
                value={form.confirmPassword}
                onChange={handleChange}
                disabled={loading}
                required
              />
              <svg style={styles.inputIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              <button 
                type="button" 
                style={styles.passwordToggle}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                tabIndex={-1}
              >
                {showConfirmPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
            disabled={loading}
          >
            {loading ? (
              <>
                <span style={styles.spinner}></span>
                Creating Account...
              </>
            ) : (
              "Create Account"
            )}
          </button>
        </form>

        <div style={styles.footer}>
          Already have an account?{" "}
          <Link to="/login" style={styles.link}>Sign In</Link>
        </div>
      </div>
    </div>
  );
}
